import { useTranslation } from 'react-i18next';
import { CircleHelp } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Badge } from './Badge';
import { statusLabelKey, statusSpec, type StatusMachine, type StatusOf } from './statusMap';

export interface StatusBadgeProps<M extends StatusMachine> {
  machine: M;
  state: StatusOf<M> | (string & {});
  className?: string;
}

/**
 * Localized badge for a state-machine value. The state code is never shown to users;
 * unknown states render a neutral badge with a generic label rather than leaking the code.
 */
export function StatusBadge<M extends StatusMachine>({ machine, state, className }: StatusBadgeProps<M>) {
  const { t } = useTranslation('common');
  const spec = statusSpec(machine, state);
  if (!spec) {
    return (
      <Badge tone="neutral" icon={CircleHelp} className={className}>
        {t('status.unknown')}
      </Badge>
    );
  }
  return (
    <Badge
      tone={spec.tone}
      appearance={spec.appearance}
      icon={spec.pulse ? undefined : spec.icon}
      pulse={spec.pulse}
      iconClassName={cn(spec.spin && 'spin', spec.goldIcon && 'text-accent-icon')}
      className={className}
    >
      {t(statusLabelKey(machine, state) as 'status.unknown')}
    </Badge>
  );
}
